'use client';

import { toast } from 'sonner';
import { randomInteger } from 'remeda';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ThemeProvider } from 'next-themes';
import { useSearchParams } from 'next/navigation';
import { useEffect, type JSX, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, signOut, useSession } from 'next-auth/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ConfirmModal, Toaster } from '~/components';
import { getQueryClient } from '~/utils/queryClient';

interface IProvidersProps {
  children: ReactNode;
}

export function LoadingState({ children }: IProvidersProps): ReactNode {
  const { status } = useSession();

  useEffect(() => {
    const handleSignOut = async () => {
      const remember = localStorage.getItem('rememberMe');
      const wasLoggedInWithoutRemember = localStorage.getItem(
        'wasLoggedInWithoutRemember',
      );

      const shouldSignOut = remember === 'false' && !wasLoggedInWithoutRemember;

      if (shouldSignOut) {
        localStorage.removeItem('rememberMe');
        await signOut();
      }

      if (wasLoggedInWithoutRemember) {
        localStorage.removeItem('wasLoggedInWithoutRemember');
      }
    };

    handleSignOut();
  }, []);

  if (status === 'loading') {
    return (
      <Loader2 className='animate-spin fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-20 stroke-illicitGreen-500' />
    );
  }

  return children;
}

export function Providers({ children }: IProvidersProps): JSX.Element {
  const queryClient = getQueryClient();
  const searchParams = useSearchParams();
  const welcome = searchParams.has('welcome');
  const t = useTranslations();

  useEffect(() => {
    //TODO: temporary check, need to improve the condition
    if (welcome && !sessionStorage.getItem('hasShownWelcomeToast')) {
      const variant = randomInteger(1, 5);
      toast.success(t(`Onboarding.toast.variant-${variant}`));
      sessionStorage.setItem('hasShownWelcomeToast', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        enableSystem
        attribute='class'
        defaultTheme='light'
        disableTransitionOnChange
      >
        <Toaster />
        <SessionProvider>
          <LoadingState>{children}</LoadingState>
        </SessionProvider>
        <ConfirmModal />
      </ThemeProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}
